/**
 * Хранилище.
 *
 * Список идёт ПО ТЗ, а не по типам файлов. Строка «исходники, 12 ТЗ,
 * 96 ГБ» бесполезна: по ней невозможно решить, что оставить.
 * Здесь видно название, сколько видео, объём с разбивкой и когда
 * что удалится — а галочками можно закрепить нужное одним действием.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Row, Tabs, toast } from '../components/ui';
import { bytes, dateTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import type { StorageTask } from '../lib/types';

type Usage = {
  totalBytes: number;
  byKind: Record<string, { bytes: number; count: number }>;
  pinned: { bytes: number; count: number };
};

type Tab = 'soon' | 'all' | 'pinned';

const COLORS: Record<string, string> = {
  FINAL: '#7C5CFF',
  MATERIAL: '#4F8AF5',
  PROXY: '#3DDC97',
  THUMB: '#F5B54A',
};
const NAMES: Record<string, string> = {
  FINAL: 'Финалы',
  MATERIAL: 'Исходники',
  PROXY: 'Прокси',
  THUMB: 'Превью',
};

export function StoragePage() {
  const { settings } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('soon');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: usage } = useQuery({
    queryKey: ['storage-usage'],
    queryFn: () => api.get<Usage>('/api/storage/usage'),
  });

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['retention', tab],
    queryFn: () =>
      api.get<StorageTask[]>(`/api/storage/retention${tab === 'soon' ? '?days=3' : ''}`),
  });

  const pin = useMutation({
    mutationFn: (input: { taskIds: string[]; pinned: boolean; scope?: 'all' | 'final_only' }) =>
      api.post('/api/storage/pin-many', input),
    onSuccess: (_res, input) => {
      void qc.invalidateQueries({ queryKey: ['retention'] });
      void qc.invalidateQueries({ queryKey: ['storage-usage'] });
      setSelected(new Set());
      toast(
        !input.pinned
          ? 'Откреплено'
          : input.scope === 'final_only'
            ? `Сохранено финалов и обложек: ${input.taskIds.length} ТЗ`
            : `Сохранено целиком: ${input.taskIds.length} ТЗ`,
        input.pinned ? '📌' : '○',
      );
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const rows = (tasks ?? []).filter((t) =>
    tab === 'pinned' ? t.pinned : tab === 'soon' ? !t.pinned : true,
  );

  const selectedSize = rows
    .filter((t) => selected.has(t.taskId))
    .reduce((s, t) => s + t.totalBytes, 0);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = rows.length > 0 && rows.every((t) => selected.has(t.taskId));

  return (
    <>
      <PageHeader
        title="Хранилище"
        sub={usage ? `занято ${bytes(usage.totalBytes)}` : undefined}
      />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            {/* Полоса занятого места */}
            <div className="card">
              <div className="h-3 bg-[#1A1A24] rounded-md overflow-hidden flex">
                {usage &&
                  Object.entries(usage.byKind).map(([kind, v]) => (
                    <div
                      key={kind}
                      style={{
                        width: `${(v.bytes / Math.max(usage.totalBytes, 1)) * 100}%`,
                        background: COLORS[kind] ?? '#444',
                      }}
                      title={`${NAMES[kind] ?? kind}: ${bytes(v.bytes)}`}
                    />
                  ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 mt-4">
                {usage &&
                  Object.entries(usage.byKind).map(([kind, v]) => (
                    <div key={kind}>
                      <div className="flex items-center gap-2 text-[12.5px] text-muted">
                        <span
                          className="w-2 h-2 rounded-sm"
                          style={{ background: COLORS[kind] ?? '#444' }}
                        />
                        {NAMES[kind] ?? kind}
                      </div>
                      <div className="mono font-extrabold text-[19px] mt-1">
                        {bytes(v.bytes)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="mt-5">
              <Tabs
                value={tab}
                onChange={(t) => {
                  setTab(t);
                  setSelected(new Set());
                }}
                items={[
                  { key: 'soon', label: 'Скоро удалится' },
                  { key: 'all', label: 'Все ТЗ' },
                  { key: 'pinned', label: 'Закреплённые' },
                ]}
              />
            </div>

            {isLoading ? (
              <Loading rows={4} />
            ) : rows.length === 0 ? (
              <div className="card">
                <Empty
                  title="Пусто"
                  hint={
                    tab === 'pinned'
                      ? 'Ничего не закреплено'
                      : 'В ближайшие дни ничего не удалится'
                  }
                />
              </div>
            ) : (
              <div className="card p-0 overflow-hidden">
                <table className="w-full text-[13.5px]">
                  <thead>
                    <tr className="text-[10.5px] tracking-[0.11em] text-muted2 font-bold uppercase">
                      <th className="w-10 px-4 pt-4 pb-2.5 border-b border-line">
                        <Check
                          on={allSelected}
                          onChange={() =>
                            setSelected(allSelected ? new Set() : new Set(rows.map((t) => t.taskId)))
                          }
                        />
                      </th>
                      <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">ТЗ</th>
                      <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">
                        Что удалится
                      </th>
                      <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">Объём</th>
                      <th className="w-12 px-4 pt-4 pb-2.5 border-b border-line" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr
                        key={t.taskId}
                        className={selected.has(t.taskId) ? 'bg-accent/[0.05]' : ''}
                      >
                        <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                          <Check on={selected.has(t.taskId)} onChange={() => toggle(t.taskId)} />
                        </td>
                        <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                          <div className="font-bold text-sm">{t.title}</div>
                          <div className="text-[11.5px] text-muted2 mt-0.5">
                            ТЗ #{t.number} · {t.videos} видео
                          </div>
                        </td>
                        <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                          {t.pinned ? (
                            <span className="text-warn text-[12.5px]">
                              📌 закреплено, не удалится
                            </span>
                          ) : (
                            <div className="text-[12.5px] text-muted">
                              {t.nextDeletionAt
                                ? `ближайшее — ${dateTime(t.nextDeletionAt)}`
                                : 'после приёмки'}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right">
                          <div className="mono font-bold">{bytes(t.totalBytes)}</div>
                          <div className="text-[11px] text-muted2 mono">
                            {t.materialBytes ? `${bytes(t.materialBytes)} + ` : ''}
                            {bytes(t.finalBytes)}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right">
                          <button
                            className={`text-base transition-opacity ${
                              t.pinned ? 'opacity-100' : 'opacity-30 hover:opacity-75'
                            }`}
                            title={t.pinned ? 'Открепить' : 'Закрепить навсегда'}
                            onClick={() =>
                              pin.mutate({
                                taskIds: [t.taskId],
                                pinned: !t.pinned,
                                scope: 'final_only',
                              })
                            }
                          >
                            📌
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {selected.size > 0 && (
                  <div className="sticky bottom-0 bg-surface border-t border-line px-4 py-3.5 flex items-center gap-3">
                    <b>
                      Выбрано {selected.size} · {bytes(selectedSize)}
                    </b>
                    <div className="flex-1" />
                    <button className="btn btn-sm" onClick={() => setSelected(new Set())}>
                      Снять выбор
                    </button>
                    {/* Два разных действия вместо одного: чаще всего нужно
                        сохранить именно финал с обложкой, а исходники пусть уходят */}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        pin.mutate({ taskIds: [...selected], pinned: true, scope: 'final_only' })
                      }
                      disabled={pin.isPending}
                      title="Исходники удалятся по обычному сроку"
                    >
                      📌 Финал и обложку
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() =>
                        pin.mutate({ taskIds: [...selected], pinned: true, scope: 'all' })
                      }
                      disabled={pin.isPending}
                      title="Занимает в разы больше места"
                    >
                      Всё целиком
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="sec !mt-0">Правила хранения</div>
            <div className="card">
              <Row label="Исходники">{settings?.retention.materialDays ?? 14} дней</Row>
              <Row label="Финалы">бессрочно</Row>
              <Row label="Прокси 720p">бессрочно</Row>
              <Row label="Обложки и превью">бессрочно</Row>
            </div>
            <div className="text-[11.5px] text-muted2 mt-3.5 px-1 leading-relaxed">
              Удаляются только исходники, и отсчёт идёт от приёмки конкретного
              видео, а не от создания ТЗ. Всё остальное хранится бессрочно.
              Закреплённое ТЗ не удаляется вовсе — ни исходники, ни финал.
            </div>

            <div className="sec">Закреплено навсегда</div>
            <div className="card">
              <div className="mono text-2xl font-extrabold">
                {usage ? bytes(usage.pinned.bytes) : '—'}
              </div>
              <div className="text-[11.5px] text-muted2 mt-1">
                {usage?.pinned.count ?? 0} файлов
              </div>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function Check({ on, onChange }: { on: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`w-[17px] h-[17px] rounded-[5px] border-[1.5px] grid place-items-center text-[11px]
                  text-white transition-colors ${
                    on ? 'bg-accent border-accent' : 'bg-bg border-[#3A3A4E] hover:border-accent'
                  }`}
    >
      {on && '✓'}
    </button>
  );
}
